import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router, Routes } from '@angular/router';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Subject } from 'rxjs';
import { AppSettings } from '../constants';
import { AssetUIModel, FilterFormDataModel, NavigationItem, TableColumn } from '../models';
import { UserAccessService } from '../user-access/user-access.service';
import { emailRegexp } from './app-validators';
import { StorageUtilityLib } from './storage-utility';

export interface HeaderModel {
  header: string;
  subHeader?: { id: string; name: string };
  routeLinkPath?: string;
}

@Injectable({ providedIn: 'root' })
export class CommonUtilityLib {
  private static STORE_PREFIX = 'COMMON_VALUE_';

  routingObject: NavigationItem = {} as NavigationItem;
  headerText = new Subject<HeaderModel | null>();
  headerText$ = this.headerText.asObservable();

  navigationItems = AppSettings.APP_CONFIG.navigation;

  constructor(
    private router: Router,
    private storageUtility: StorageUtilityLib,
    private userAccessService: UserAccessService,
  ) {
    this.navigationItems.forEach((navItem) => {
      navItem.items.forEach((subItem) => {
        if (this.router.url.indexOf('/' + subItem.routerLink) != -1) {
          this.routingObject = subItem;
        }
      });
    });
  }

  put<T>(key: string, value: T): T {
    if (value) {
      this.storageUtility.setLocalStorage(CommonUtilityLib.STORE_PREFIX + key, value);
    } else {
      this.storageUtility.clearLocalStorage(CommonUtilityLib.STORE_PREFIX + key);
    }
    return value;
  }

  get<T>(key: string): T | null {
    return this.storageUtility.getLocalStorageNullable<T>(CommonUtilityLib.STORE_PREFIX + key);
  }

  /**
   * Setting routing object
   *
   * @param {*} data
   * @returns
   *
   * @memberOf CommonUtilityLib
   */
  routingObjectSetter(data: AnyType) {
    this.routingObject = data;
  }

  /**
   * Getting routing object
   *
   * @param {*}
   * @returns
   *
   * @memberOf CommonUtilityLib
   */
  routingObjectGetter(): NavigationItem {
    if (this.isEmptyObject(this.routingObject)) {
      return AppSettings.APP_CONFIG.navigation[0].items[0];
    }
    return this.routingObject;
  }

  /**
   * Check object is empty or not
   *
   * @param {*} obj
   * @returns {*}
   * @memberof CommonUtilitiesLib
   */
  isEmptyObject(obj: AnyType): boolean {
    return obj && obj.constructor === Object && Object.keys(obj).length === 0;
  }

  /**
   * Change header component text
   *
   * @param {*} newHeader
   * @returns
   * @memberof CommonUtilitiesLib
   */
  emitNewHeaderline(header: string | null, subHeader?: AnyType, routeLinkPath?: string) {
    if (header !== null) {
      this.headerText.next({ header, subHeader, routeLinkPath });
    } else {
      this.headerText.next(null);
    }
  }

  /**
   * Check text content length
   *
   * @param {*} event
   * @returns {boolean, number}
   * @memberof CommonUtilitiesLib
   */
  textContentLength(event: AnyType): { checkDescLength: boolean; descLength: number } {
    const descLength = event.srcElement.textContent.length;
    return { checkDescLength: descLength > 5000 ? true : false, descLength };
  }

  /**
   * build route items
   *
   * @param {Routes} routes
   * @param {string} clientRoutes
   * @returns {*}
   * @memberof CommonUtilitiesLib
   */
  buildNavItems(
    routes: Routes,
    clientRoutes?: string[],
    asset?: AssetUIModel,
    collectionOwner?: boolean,
    brandingKey?: CustomClients,
  ): {
    path: string;
    label: string;
    alternateLabel: string;
  }[] {
    return routes
      .filter((route) => {
        if (route.data) {
          // exclude tabs for specific asset types
          if (asset && route.data['excludeAssetTypes'] && route.data['excludeAssetTypes'].includes(asset.assetType)) {
            return false;
          }
          // exclude tabs if user accessibility fails
          if (route.data['role'] || route.data['roles']) {
            const roles = [].concat(route.data['role'] ?? route.data['roles']);
            const authorized = roles.every((role: AnyType) => this.userAccessService.hasRole(role));
            if (!authorized) {
              return false;
            }
          }
          if (route.data['collectionOwnerCheck']) {
            if (collectionOwner) {
              return true;
            }
            return false;
          }
          if (route.data['clientsSpecific']) {
            if (clientRoutes?.includes(route.path || '')) {
              return true;
            }
            return false;
          }
          if (route.data['customClients']) {
            const customClients: string[] = route.data['customClients'];
            if (customClients.length && customClients.includes(brandingKey || CustomClients.Standard)) {
              return true;
            }
            return false;
          }

          return true;
        }
        return false;
      })
      .map(({ path = '', data }) => ({
        path: path,
        label: typeof data?.['label'] === 'function' ? data?.['label'](asset, brandingKey) : data?.['label'],
        alternateLabel: data?.['alternateLabel'] ? data?.['alternateLabel'] : '',
      }));
  }

  /**
   * Remove object from array
   * @param array
   * @param toRemove
   */
  removeObjectFromArray(array: AnyType[], toRemove: AnyType) {
    /* obj._id == event.value || obj.id == event.value */
    const index = array.findIndex((arr) => arr.id === toRemove || arr._id === toRemove || arr.name === toRemove || arr === toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
    return array;
  }

  /**
   * Count the number of filter applied
   * @param filterObjectFilters
   * @returns
   */
  filterDataCount(filterObjectFilters: AnyType, skipPropertyCount?: string[]) {
    let appliedFilters = 0;
    for (const key in filterObjectFilters) {
      if (filterObjectFilters[key] && !key.includes('$or') && !skipPropertyCount?.includes(key) && !key.startsWith('searchText[')) {
        appliedFilters++;
      }
    }
    return appliedFilters;
  }

  /**
   * Convert string to camelCase string
   * @param sentenceCase
   * @returns
   */
  toCamelCase(sentenceCase: string) {
    let out = '';
    sentenceCase.split(' ').forEach(function (el, idx) {
      const add = el.toLowerCase();
      out += idx === 0 ? add : add[0] && add[0].toUpperCase() + add.slice(1);
    });
    return out;
  }

  /**
   * Hide columns based on column key
   * @param keyToHide
   * @param columns
   * @returns
   */
  hideColumn(keyToHide: string[], columns: TableColumn[]): TableColumn[] {
    return columns.filter((col) => {
      return !keyToHide.includes(col.columnDef);
    });
  }

  /**
   * Find form field index
   * @param matchingKey
   * @returns
   */
  findArrayIndex(filterProjectFormData: FilterFormDataModel, matchingKey: string) {
    return filterProjectFormData.formFields.findIndex((obj) => obj.formFieldName == matchingKey);
  }

  /**
   * Validate email address
   * @param email
   * @returns
   */
  validateEmail(email: string): boolean {
    return emailRegexp.test(String(email).toLowerCase());
  }

  getRequiredControls(formControl: UntypedFormGroup): string | undefined {
    if (!formControl) return undefined;
    const controls = formControl.controls;
    for (const key in controls) {
      if (Object.hasOwnProperty.call(controls, key)) {
        const control = controls[key];
        if (control.invalid) {
          return key;
        }
      }
    }
    return undefined;
  }
}
